import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Shield, Trash2 } from 'lucide-react';
import type { AdminUser, AdminUserRole, UserRoleAssign } from '../../generated/api/types.gen.js';
import { zUserRoleAssign } from '../../generated/api/zod.gen.js';
import {
  assignUserRoleMutation,
  getAdminUserQueryKey,
  listAdminUsersQueryKey,
  removeUserRoleMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Button, Card, ConfirmDialog, DataTable, EmptyState, useToast } from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { Form, NumberFormField, SelectFormField, SubmitButton, useZodForm } from '../../components/form/index.js';
import { Can } from '../../components/auth/Can.js';
import { ASSIGNABLE_ROLES, formatDate, roleBadgeVariant, roleLabel } from './user-labels.js';

interface UserRolesTabProps {
  user: AdminUser;
}

const MANAGE_USERS = ['CanManageUsers', 'CanManageOrgMembers'];
const SCOPE_LABEL: Record<AdminUserRole['scope'], string> = { global: 'Global', org: 'Organization', team: 'Team' };
const ROLE_OPTIONS = ASSIGNABLE_ROLES.map(({ value, label }) => ({ value, label }));

/** A person's roles, the form to add one, and the organization they belong to. */
export const UserRolesTab: React.FC<UserRolesTabProps> = ({ user }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [removing, setRemoving] = useState<AdminUserRole | null>(null);

  const form = useZodForm(zUserRoleAssign, {
    defaultValues: { role: 'viewer', orgId: user.organization?.orgId ?? undefined },
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getAdminUserQueryKey({ path: { id: user.id } }) }),
      queryClient.invalidateQueries({ queryKey: listAdminUsersQueryKey() }),
    ]);

  const assign = useMutation(assignUserRoleMutation());
  const remove = useMutation({
    ...removeUserRoleMutation(),
    onSuccess: async () => {
      setRemoving(null);
      notify('Role removed');
      await refresh();
    },
    onError: () => notify('Could not remove the role', 'danger'),
  });

  const addRole = async (body: UserRoleAssign) => {
    await assign.mutateAsync({ path: { id: user.id }, body });
    await refresh();
    notify('Role assigned');
    form.reset({ role: 'viewer', orgId: body.orgId });
  };

  const roles = user.roles ?? [];

  const columns: DataTableColumn<AdminUserRole>[] = [
    {
      key: 'role',
      header: 'Role',
      render: (role) => <Badge variant={roleBadgeVariant(role.roleName)}>{roleLabel(role.roleName)}</Badge>,
    },
    {
      key: 'scope',
      header: 'Scope',
      render: (role) => <span className="text-sm text-body">{SCOPE_LABEL[role.scope]}</span>,
    },
    {
      key: 'grantedAt',
      header: 'Granted',
      render: (role) => <span className="text-sm text-muted">{formatDate(role.grantedAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '64px',
      render: (role) => (
        <Can policies={MANAGE_USERS} mode="any">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Remove the ${roleLabel(role.roleName)} role`}
            onClick={() => setRemoving(role)}
          >
            <Trash2 size={14} aria-hidden="true" />
          </Button>
        </Can>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <DataTable
        framed
        columns={columns}
        rows={roles}
        rowKey={(role) => role.id}
        aria-label="Roles"
        empty={
          <EmptyState
            icon={Shield}
            title="No roles assigned"
            description="Without a role this person can sign in but cannot see anything. Add one below."
          />
        }
      />

      <Can policies={MANAGE_USERS} mode="any">
        <Card title="Add a role">
          <Form form={form} onSubmit={addRole} className="sm:flex-row sm:items-start">
            <SelectFormField name="role" label="Role" options={ROLE_OPTIONS} required className="sm:flex-1" />
            <NumberFormField
              name="orgId"
              label="Organization ID"
              description="Defaults to this person's organization."
              minValue={1}
              required
              className="sm:flex-1"
            />
            <SubmitButton className="sm:mt-6">Assign role</SubmitButton>
          </Form>
        </Card>
      </Can>

      <Card title="Organization">
        {user.organization ? (
          <div className="flex items-center gap-3">
            <Building2 size={18} className="text-muted" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-heading">{user.organization.orgName}</p>
              <p className="text-xs text-muted">{user.organization.orgSlug}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted">This person does not belong to an organization.</p>
        )}
      </Card>

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate({ path: { id: user.id, roleId: removing.id } })}
        title="Remove role"
        message={`Remove the ${removing ? roleLabel(removing.roleName) : ''} role from this person? They lose its access straight away.`}
        confirmLabel="Remove role"
        loading={remove.isPending}
      />
    </div>
  );
};
